import { supabase } from '../supabase';
import type {
  Checkin,
  CheckinCounterMap,
  Mission,
  MissionCtaType,
  MissionDateValue,
  MissionEvaluation,
  MissionObjective,
  MissionRuleType,
} from './types';
import { normalizePartnerCategory } from '../constants';

/**
 * Row do Postgres. Dynamic by design — mappers abaixo validam cada campo.
 * Mesma estratégia do dataService.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbRow = Record<string, any>;

type UserMissionState = {
  uid?: string;
  completedMissions?: string[];
  checkinCount?: number;
  partnerCheckinCounts?: CheckinCounterMap;
  categoryCheckinCounts?: CheckinCounterMap;
  name?: string;
  phone?: string;
  email?: string;
  cpf?: string;
};

export const getPartnerCheckinCounterKey = (partnerId?: string | null) =>
  String(partnerId || '').trim();

export const getCategoryCheckinCounterKey = (category?: string | null) =>
  String(normalizePartnerCategory(category)).trim().toLowerCase();

export const resolveMissionObjective = (mission: Partial<Mission>): MissionObjective => {
  if (mission.missionObjective) return mission.missionObjective;
  if (mission.ruleType === 'profile_complete') return 'profile_complete';
  if (mission.startsAt || mission.endsAt) {
    if (mission.targetCategory) return 'category_period_checkins';
    if (mission.targetPartnerId) return 'partner_period_checkins';
    return 'period_checkins';
  }
  if (mission.targetCategory) return 'category_specific';
  if (mission.targetPartnerId) return 'partner_specific';
  return 'total_checkins';
};

const LEGACY_MISSION_MAP: Record<
  string,
  { ruleType: MissionRuleType; targetValue: number; ctaType: MissionCtaType; ctaLabel: string }
> = {
  checkin: {
    ruleType: 'checkin_count',
    targetValue: 1,
    ctaType: 'explore',
    ctaLabel: 'Ir para explorar',
  },
  cpf: { ruleType: 'profile_complete', targetValue: 4, ctaType: 'profile', ctaLabel: 'Ver perfil' },
  first_checkin: {
    ruleType: 'checkin_count',
    targetValue: 1,
    ctaType: 'explore',
    ctaLabel: 'Ir para explorar',
  },
  three_checkins: {
    ruleType: 'checkin_count',
    targetValue: 3,
    ctaType: 'explore',
    ctaLabel: 'Continuar missão',
  },
  complete_profile: {
    ruleType: 'profile_complete',
    targetValue: 4,
    ctaType: 'profile',
    ctaLabel: 'Ver perfil',
  },
};

export const normalizeMission = (mission: Partial<Mission> & { id: string }): Mission => {
  const legacyConfig = mission.type ? LEGACY_MISSION_MAP[mission.type] : undefined;

  return {
    id: mission.id,
    title: mission.title || 'Missão',
    description: mission.description || '',
    icon: mission.icon || '🎯',
    points: mission.points || 0,
    active: mission.active !== false,
    missionGroup: mission.missionGroup || 'base',
    missionObjective: resolveMissionObjective(mission),
    ruleType: mission.ruleType || legacyConfig?.ruleType,
    targetValue: mission.targetValue ?? legacyConfig?.targetValue ?? 1,
    targetPartnerId: mission.targetPartnerId,
    targetCategory: mission.targetCategory,
    startsAt: mission.startsAt,
    endsAt: mission.endsAt,
    displayOrder: mission.displayOrder ?? 999,
    showInHome: mission.showInHome ?? false,
    showInProfile: mission.showInProfile ?? true,
    ctaType: mission.ctaType || legacyConfig?.ctaType || 'none',
    ctaLabel: mission.ctaLabel || legacyConfig?.ctaLabel || '',
    type: mission.type,
  };
};

const parseMissionDate = (value?: MissionDateValue): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const getMissionPeriodWindow = (mission: Mission) => ({
  startsAt: parseMissionDate(mission.startsAt),
  endsAt: parseMissionDate(mission.endsAt),
});

const isPeriodMission = (objective?: MissionObjective) =>
  objective === 'period_checkins' ||
  objective === 'partner_period_checkins' ||
  objective === 'category_period_checkins';

const getCurrentValueFromAggregates = (mission: Mission, userData: UserMissionState) => {
  if (mission.ruleType === 'profile_complete') {
    const fields = [userData.name, userData.phone, userData.email, userData.cpf];
    return fields.filter(Boolean).length;
  }
  if (mission.missionObjective === 'partner_specific') {
    const partnerKey = getPartnerCheckinCounterKey(mission.targetPartnerId);
    return partnerKey ? userData.partnerCheckinCounts?.[partnerKey] || 0 : 0;
  }
  if (mission.missionObjective === 'category_specific') {
    const categoryKey = getCategoryCheckinCounterKey(mission.targetCategory);
    return categoryKey ? userData.categoryCheckinCounts?.[categoryKey] || 0 : 0;
  }
  return userData.checkinCount || 0;
};

const buildMissionEvaluation = (
  mission: Mission,
  currentValue: number,
  targetValue: number,
  forceCompleted = false,
): MissionEvaluation => {
  const isCompleted = forceCompleted || currentValue >= targetValue;
  const status = isCompleted ? 'completed' : currentValue > 0 ? 'in_progress' : 'pending';
  return {
    mission,
    status,
    currentValue,
    targetValue,
    progressPercent:
      targetValue > 0 ? Math.min(100, Math.max(0, (currentValue / targetValue) * 100)) : 0,
    isCompleted,
  };
};

const partnerCategoryCache = new Map<string, string>();

const getPartnerCategoryKeyById = async (partnerId?: string | null) => {
  const partnerKey = getPartnerCheckinCounterKey(partnerId);
  if (!partnerKey) return '';
  if (partnerCategoryCache.has(partnerKey)) {
    return partnerCategoryCache.get(partnerKey) || '';
  }
  const { data } = await supabase
    .from('partners')
    .select('category')
    .eq('id', partnerKey)
    .maybeSingle();
  const categoryKey = data ? getCategoryCheckinCounterKey(data.category) : '';
  partnerCategoryCache.set(partnerKey, categoryKey);
  return categoryKey;
};

const getCheckinCategoryKey = async (checkin: Partial<Checkin> & DbRow) => {
  const directCategoryKey = getCategoryCheckinCounterKey(
    checkin.partnerCategoryKey || checkin.partnerCategory,
  );
  if (directCategoryKey) return directCategoryKey;
  return getPartnerCategoryKeyById(checkin.partnerId);
};

const countMissionCheckinsInPeriod = async (userId: string, mission: Mission) => {
  const { startsAt, endsAt } = getMissionPeriodWindow(mission);
  let qb = supabase
    .from('checkins')
    .select('partner_id, partner_category, partner_category_key, timestamp')
    .eq('user_id', userId);

  if (startsAt) qb = qb.gte('timestamp', startsAt.toISOString());
  if (endsAt) qb = qb.lte('timestamp', endsAt.toISOString());

  const { data, error } = await qb.order('timestamp', { ascending: true });
  if (error) {
    console.warn('[missionService] countMissionCheckinsInPeriod error:', error.message);
    return 0;
  }

  const partnerKey = getPartnerCheckinCounterKey(mission.targetPartnerId);
  const categoryKey = getCategoryCheckinCounterKey(mission.targetCategory);
  let total = 0;

  for (const row of data ?? []) {
    const checkin = {
      partnerId: row.partner_id,
      partnerCategory: row.partner_category,
      partnerCategoryKey: row.partner_category_key,
    };
    if (mission.missionObjective === 'partner_period_checkins') {
      if (getPartnerCheckinCounterKey(checkin.partnerId) !== partnerKey) continue;
    }
    if (mission.missionObjective === 'category_period_checkins') {
      const resolvedCategoryKey = await getCheckinCategoryKey(checkin);
      if (resolvedCategoryKey !== categoryKey) continue;
    }
    total += 1;
  }
  return total;
};

export const evaluateMission = (
  missionInput: Partial<Mission> & { id: string },
  userData: UserMissionState,
): MissionEvaluation => {
  const mission = normalizeMission(missionInput);
  const completedMissions = userData.completedMissions || [];
  const targetValue = mission.targetValue || 1;

  if (completedMissions.includes(mission.id)) {
    return buildMissionEvaluation(mission, targetValue, targetValue, true);
  }

  const currentValue = isPeriodMission(mission.missionObjective)
    ? 0
    : getCurrentValueFromAggregates(mission, userData);

  return buildMissionEvaluation(
    mission,
    currentValue,
    mission.ruleType === 'profile_complete' ? 4 : targetValue,
  );
};

export const evaluateMissionForUser = async (
  missionInput: Partial<Mission> & { id: string },
  userData: UserMissionState,
  userId?: string,
): Promise<MissionEvaluation> => {
  const mission = normalizeMission(missionInput);
  const completedMissions = userData.completedMissions || [];
  const targetValue = mission.ruleType === 'profile_complete' ? 4 : mission.targetValue || 1;

  if (completedMissions.includes(mission.id)) {
    return buildMissionEvaluation(mission, targetValue, targetValue, true);
  }

  if (isPeriodMission(mission.missionObjective)) {
    if (!userId) return buildMissionEvaluation(mission, 0, targetValue);
    const currentValue = await countMissionCheckinsInPeriod(userId, mission);
    return buildMissionEvaluation(mission, currentValue, targetValue);
  }

  return buildMissionEvaluation(
    mission,
    getCurrentValueFromAggregates(mission, userData),
    targetValue,
  );
};

export const evaluateMissionsForUser = async (
  missions: Array<Partial<Mission> & { id: string }>,
  userData: UserMissionState,
  userId?: string,
) => {
  const evaluations = await Promise.all(
    missions.map((mission) => evaluateMissionForUser(mission, userData, userId || userData.uid)),
  );
  return evaluations.sort(
    (a, b) => (a.mission.displayOrder || 999) - (b.mission.displayOrder || 999),
  );
};

/** Mapeia Mission/MissionForm (camelCase) → row Postgres (snake_case). */
export function missionToRow(m: Partial<Mission>): DbRow {
  const out: DbRow = {};
  if (m.title !== undefined) out.title = m.title;
  if (m.description !== undefined) out.description = m.description;
  if (m.icon !== undefined) out.icon = m.icon;
  if (m.points !== undefined) out.points = m.points;
  if (m.active !== undefined) out.active = m.active;
  if (m.missionGroup !== undefined) out.mission_group = m.missionGroup;
  if (m.missionObjective !== undefined) out.mission_objective = m.missionObjective;
  if (m.ruleType !== undefined) out.rule_type = m.ruleType;
  if (m.targetValue !== undefined) out.target_value = m.targetValue;
  if (m.targetPartnerId !== undefined) out.target_partner_id = m.targetPartnerId || null;
  if (m.targetCategory !== undefined) out.target_category = m.targetCategory || null;
  if (m.startsAt !== undefined)
    out.starts_at = m.startsAt instanceof Date ? m.startsAt.toISOString() : m.startsAt || null;
  if (m.endsAt !== undefined)
    out.ends_at = m.endsAt instanceof Date ? m.endsAt.toISOString() : m.endsAt || null;
  if (m.displayOrder !== undefined) out.display_order = m.displayOrder;
  if (m.showInHome !== undefined) out.show_in_home = m.showInHome;
  if (m.showInProfile !== undefined) out.show_in_profile = m.showInProfile;
  if (m.ctaType !== undefined) out.cta_type = m.ctaType;
  if (m.ctaLabel !== undefined) out.cta_label = m.ctaLabel || null;
  if (m.type !== undefined) out.type = m.type || null;
  return out;
}

/** Mapeia row de `missions` (snake_case) → shape esperado (camelCase). */
export function missionRowToInput(r: DbRow): Partial<Mission> & { id: string } {
  return {
    id: r.id,
    title: r.title,
    description: r.description,
    icon: r.icon,
    points: r.points,
    active: r.active,
    missionGroup: r.mission_group,
    missionObjective: r.mission_objective,
    ruleType: r.rule_type,
    targetValue: r.target_value,
    targetPartnerId: r.target_partner_id,
    targetCategory: r.target_category,
    startsAt: r.starts_at,
    endsAt: r.ends_at,
    displayOrder: r.display_order,
    showInHome: r.show_in_home,
    showInProfile: r.show_in_profile,
    ctaType: r.cta_type,
    ctaLabel: r.cta_label,
    type: r.type,
  };
}

/** Mapeia row de `users` (snake_case) → UserMissionState. */
function userRowToState(r: DbRow): UserMissionState {
  return {
    uid: r.id,
    completedMissions: r.completed_missions ?? [],
    checkinCount: r.checkin_count ?? 0,
    partnerCheckinCounts: r.partner_checkin_counts ?? {},
    categoryCheckinCounts: r.category_checkin_counts ?? {},
    name: r.name ?? undefined,
    phone: r.phone ?? undefined,
    email: r.email ?? undefined,
    cpf: r.cpf ?? undefined,
  };
}

export const checkAndAwardMissions = async (userId: string, actionType?: string) => {
  try {
    const { data: missionsData, error: missionsErr } = await supabase
      .from('missions')
      .select('*')
      .eq('active', true);

    if (missionsErr) throw missionsErr;
    if (!missionsData || missionsData.length === 0) {
      return { awardedPoints: 0, completedMissions: [], completedMissionIds: [], actionType };
    }

    const { data: userRow, error: userErr } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (userErr) throw userErr;
    if (!userRow) {
      return { awardedPoints: 0, completedMissions: [], completedMissionIds: [], actionType };
    }

    const userData = userRowToState(userRow);
    const evaluations = await evaluateMissionsForUser(
      missionsData.map(missionRowToInput),
      userData,
      userId,
    );

    let totalPointsToAward = 0;
    const newlyCompletedMissions: Mission[] = [];
    const missionIdsToMark: string[] = [];

    for (const evaluation of evaluations) {
      if (!evaluation.isCompleted) continue;
      if ((userData.completedMissions || []).includes(evaluation.mission.id)) continue;
      totalPointsToAward += evaluation.mission.points || 0;
      newlyCompletedMissions.push(evaluation.mission);
      missionIdsToMark.push(evaluation.mission.id);
    }

    if (missionIdsToMark.length > 0) {
      // Increment de points via RPC (atômico).
      if (totalPointsToAward > 0) {
        const { error: rpcErr } = await supabase.rpc('increment_user_counter', {
          p_user_id: userId,
          p_field: 'points',
          p_delta: totalPointsToAward,
        });
        if (rpcErr) throw rpcErr;
      }
      // Append em completed_missions: lê estado atual + concatena + grava.
      // Pequena race condition aceitável (admin não dispara isso paralelamente).
      const merged = Array.from(
        new Set([...(userData.completedMissions || []), ...missionIdsToMark]),
      );
      const { error: updateErr } = await supabase
        .from('users')
        .update({ completed_missions: merged })
        .eq('id', userId);
      if (updateErr) throw updateErr;
    }

    return {
      awardedPoints: totalPointsToAward,
      completedMissions: newlyCompletedMissions,
      completedMissionIds: missionIdsToMark,
      actionType,
    };
  } catch (error) {
    console.error('Error checking missions:', error);
    return { awardedPoints: 0, completedMissions: [], completedMissionIds: [], actionType };
  }
};
